import { useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { createManualPayoutSession } from 'api/merchants';

const ALLOWED_FUND_TYPE = ['credit', 'debit'];
const ALLOWED_TRANSFER_METHOD = ['FIAT', 'CRYPTO', 'DEBIT_REVERSAL', 'CREDIT_REVERSAL', 'BANKING_TRANSACTION_FEES'];

const INITIAL_FORM_STATE = {
  sourceAccountId: '',
  beneficiaryId: '',
  sourceAmount: '',
  receivingAmount: '',
  exchangeRate: '',
  sourceCurrency: '',
  destinationCurrency: '',
  referenceId: '',
  comments: '',
  remarks: '',
  fundType: '',
  transferMethod: '',
  beneficiaryName: '',
};

export default function InsertTransactionModal({ isOpen, onClose, merchantId }) {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const handleClose = () => {
    setFormData(INITIAL_FORM_STATE);
    onClose();
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isFormValid =
    formData.sourceAccountId &&
    formData.sourceAmount &&
    formData.receivingAmount &&
    formData.exchangeRate &&
    formData.sourceCurrency &&
    formData.destinationCurrency &&
    formData.referenceId &&
    formData.fundType &&
    formData.transferMethod;

  const handleSubmit = async () => {
    if (!isFormValid) {
      toast({
        title: 'Please fill all required fields.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });

      return;
    }

    try {
      setIsLoading(true);
      await createManualPayoutSession({
        merchant_id: merchantId,
        source_account_id: formData.sourceAccountId,
        beneficiary_id: formData.beneficiaryId,
        source_amount: parseFloat(formData.sourceAmount),
        receiving_amount: parseFloat(formData.receivingAmount),
        exchange_rate: parseFloat(formData.exchangeRate),
        source_currency: formData.sourceCurrency,
        destination_currency: formData.destinationCurrency,
        reference_id: formData.referenceId,
        comments: formData.comments,
        remarks: formData.remarks,
        fund_type: formData.fundType,
        transfer_method: formData.transferMethod,
        beneficiary_name: formData.beneficiaryName,
      });
      toast({
        title: 'Transaction inserted successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      handleClose();
    } catch (error) {
      toast({
        title: 'Failed to insert transaction.',
        description: error?.response?.data?.message || error?.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size='xl' scrollBehavior='inside'>
      <ModalOverlay />
      <ModalContent maxW='800px'>
        <ModalHeader>Insert Transaction</ModalHeader>
        <ModalCloseButton />
        <ModalBody px={8}>
          <VStack spacing={4}>
            <FormControl>
              <FormLabel>Merchant ID</FormLabel>
              <Input value={merchantId || ''} isDisabled />
            </FormControl>

            <SimpleGrid columns={2} spacing={4} w='100%'>
              <FormControl isRequired>
                <FormLabel>Source Account ID</FormLabel>
                <Input
                  placeholder='Enter Source Account ID'
                  value={formData.sourceAccountId}
                  onChange={(e) => handleChange('sourceAccountId', e.target.value)}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Reference ID</FormLabel>
                <Input
                  placeholder='Enter Reference ID'
                  value={formData.referenceId}
                  onChange={(e) => handleChange('referenceId', e.target.value)}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Fund Type</FormLabel>
                <Select
                  placeholder='Select Fund Type'
                  value={formData.fundType}
                  onChange={(e) => handleChange('fundType', e.target.value)}
                >
                  {ALLOWED_FUND_TYPE.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Transfer Method</FormLabel>
                <Select
                  placeholder='Select Transfer Method'
                  value={formData.transferMethod}
                  onChange={(e) => handleChange('transferMethod', e.target.value)}
                >
                  {ALLOWED_TRANSFER_METHOD.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Source Amount</FormLabel>
                <Input
                  type='number'
                  placeholder='0.00'
                  value={formData.sourceAmount}
                  onChange={(e) => handleChange('sourceAmount', e.target.value)}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Receiving Amount</FormLabel>
                <Input
                  type='number'
                  placeholder='0.00'
                  value={formData.receivingAmount}
                  onChange={(e) => handleChange('receivingAmount', e.target.value)}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Exchange Rate</FormLabel>
                <Input
                  type='number'
                  placeholder='1.0'
                  value={formData.exchangeRate}
                  onChange={(e) => handleChange('exchangeRate', e.target.value)}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Source Currency</FormLabel>
                <Input
                  placeholder='e.g. USD'
                  value={formData.sourceCurrency}
                  onChange={(e) => handleChange('sourceCurrency', e.target.value)}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Destination Currency</FormLabel>
                <Input
                  placeholder='e.g. EUR'
                  value={formData.destinationCurrency}
                  onChange={(e) => handleChange('destinationCurrency', e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Beneficiary ID</FormLabel>
                <Input
                  placeholder='Enter Beneficiary ID'
                  value={formData.beneficiaryId}
                  onChange={(e) => handleChange('beneficiaryId', e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Beneficiary Name</FormLabel>
                <Input
                  placeholder='Enter Beneficiary Name'
                  value={formData.beneficiaryName}
                  onChange={(e) => handleChange('beneficiaryName', e.target.value)}
                />
              </FormControl>
            </SimpleGrid>

            <FormControl>
              <FormLabel>Comments</FormLabel>
              <Textarea
                placeholder='Enter comments'
                value={formData.comments}
                onChange={(e) => handleChange('comments', e.target.value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Remarks</FormLabel>
              <Textarea
                placeholder='Enter remarks'
                value={formData.remarks}
                onChange={(e) => handleChange('remarks', e.target.value)}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter px={8}>
          <Button variant='ghost' mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button colorScheme='blue' onClick={handleSubmit} isLoading={isLoading} isDisabled={!isFormValid}>
            Submit
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
